import ApiClient from '@/services/apiClient';
import i18n from '@/i18n';
import AddressConfig from '@/models/addressConfig';
import Input from '@/models/input';
import Output from '@/models/output';
import ReaderConfig from '@/models/readerConfig';
import Scenario from '@/models/scenario';
import moment from 'moment';
import ExtensionModule from './extensionModule';
import License from './license';
import LPR from './lpr';
import SpcConfig from './spcConfig';
import Integration from './integration';
import { parseSinceDate, formatSinceDate } from '@/utils/sinceDate';

export default class Hub {
    id;
    dhcp;
    ip;
    anti_pass_back;
    anti_time_back;
    auto_protection_input;
    power_test_input;
    label;
    endpoint;
    facility_id;
    readers;
    timezone;
    serial_number;
    equipement_type;      //"Hub2", "Hub6", "BoxInteg", "BoxOnpremise"
    warranty_start_date;
    software_version;
    hardware_version;
    mac_address;          // reported by the device, undefined, null or "" while unknown
    alerts = 0;
    scenarios = [];
    hub_state = 0;
    hub_state_since = null;     // date of the last connection state change
    extensions = [];
    integrated_license;
    licenses = [];
    ajax_state = null;
    spc_state = null;
    spc_config = null;
    nvrs = [];          // video devices, loaded by the video integration page
    cameras = [];
    lprs = [];

    static constructFromObject(data, obj) {
      if (data) {
        obj = obj || new Hub();
        obj.id = ApiClient.convertToType(data['id'], 'Integer');
        obj.dhcp = ApiClient.convertToType(data['dhcp'], 'Boolean');
        obj.ip = AddressConfig.constructFromObject(data['ip']);
        obj.anti_pass_back = ApiClient.convertToType(data['anti_pass_back'], 'Boolean');
        obj.anti_time_back = ApiClient.convertToType(data['anti_time_back'], 'Integer');
        obj.auto_protection_input = Input.constructFromObject(data['auto_protection_input']);
        obj.power_test_input = Input.constructFromObject(data['power_test_input']);
        obj.label = ApiClient.convertToType(data['label'], 'String');
        obj.endpoint = ApiClient.convertToType(data['endpoint'], 'String');
        obj.facility_id = ApiClient.convertToType(data['facility_id'], 'Integer');
        obj.readers = ApiClient.convertToType(data['readers'], [ReaderConfig]);
        obj.timezone = ApiClient.convertToType(data['timezone'], 'String');
        obj.serial_number = ApiClient.convertToType(data['serial_number'], 'String');
        obj.equipement_type = ApiClient.convertToType(data['equipement_type'], 'String');
        obj.warranty_start_date = ApiClient.convertToType(data['warranty_start_date'], 'Date');
        obj.software_version = ApiClient.convertToType(data['software_version'], 'String');
        obj.hardware_version = ApiClient.convertToType(data['hardware_version'], 'String');
        obj.mac_address = ApiClient.convertToType(data['mac_address'], 'String');
        obj.scenarios = ApiClient.convertToType(data['scenarios'], [Scenario]);
        obj.hub_state = ApiClient.convertToType(data['hub_state'], 'Integer');
        obj.hub_state_since = parseSinceDate(data['hub_state_since']);
        obj.extensions = ApiClient.convertToType(data['extensions'], [ExtensionModule]) || [];
        obj.integrated_license = ApiClient.convertToType(data['integrated_license'], License);
        obj.licenses = ApiClient.convertToType(data['licenses'], [License]) || [];
        obj.ajax_state = ApiClient.convertToType(data['ajax_state'], 'Boolean') ?? null;
        obj.spc_state = ApiClient.convertToType(data['spc_state'], 'Boolean') ?? null;
        obj.spc_config = ApiClient.convertToType(data['spc_config'], SpcConfig) ?? null;
        obj.lprs = ApiClient.convertToType(data['lprs'], [LPR]) || [];
      }
      return obj;
    }

    toJSON() {
      const json = {
        anti_pass_back: this.anti_pass_back,
        anti_time_back: this.anti_pass_back? this.anti_time_back : 0,
        label: this.label,
        timezone: this.timezone,
        extensions: this.extensions,
      };
      if(this.auto_protection_input.input_number > 0) {
        json.auto_protection_input = this.auto_protection_input;
      }
      if(this.power_test_input.input_number > 0) {
        json.power_test_input = this.power_test_input;
      }
      if(this.integrated_license) {
        json.integrated_license = this.integrated_license;
      }
      return json;
    }

    clone() {
      let hub = new Hub();
      hub.id = this.id;
      hub.anti_pass_back = this.anti_pass_back;
      hub.anti_time_back = this.anti_time_back;
      hub.label = this.label;
      hub.timezone = this.timezone;
      hub.auto_protection_input = Input.constructFromObject(this.auto_protection_input);
      hub.power_test_input = Input.constructFromObject(this.power_test_input);
      hub.readers = this.readers;
      hub.equipement_type = this.equipement_type;
      hub.serial_number = this.serial_number;
      hub.warranty_start_date = this.warranty_start_date;
      hub.software_version = this.software_version;
      hub.hardware_version = this.hardware_version;
      hub.mac_address = this.mac_address;
      hub.hub_state = this.hub_state;
      hub.extensions = this.extensions;
      hub.facility_id = this.facility_id;
      hub.integrated_license = this.integrated_license;
      return hub;
    }

    update(updatedHub) {
      this.anti_pass_back = updatedHub.anti_pass_back;
      this.anti_time_back = updatedHub.anti_pass_back? updatedHub.anti_time_back : 0;
      this.label = updatedHub.label;
      this.timezone = updatedHub.timezone;
      this.auto_protection_input = updatedHub.auto_protection_input;
      this.power_test_input = updatedHub.power_test_input;
      this.integrated_license = updatedHub.integrated_license;
    }

    get getVersions() {
      return `HAXOS : ${this.software_version} - Hardware : ${this.hardware_version}`;
    }

    get getWarrantyStartDate() {
      return moment(this.warranty_start_date).format('DD/MM/YYYY');
    }

    getAvailableOutputs() {
      let outputs = [];
      switch(this.equipement_type) {
        case "Hub2": 
          outputs.push(new Output(0, "Relais auxiliaire"));
          break;
        case "Hub6": 
          outputs.push(new Output(0, "Relais auxiliaire 1"));
          outputs.push(new Output(1, "Relais auxiliaire 2"));
          break;
        default:
          return [];
      }
      this.extensions.forEach(module => {
        switch(module.module_type) {
          case "Relay":
            for(let i = 0; i <= 10; i++) {
              const label = `Relais ${i == 0? 'auxiliaire' : i} ${module.label}`;
              const output = new Output(module.serial_number * 256 + i, label);
              outputs.push(output);
            }
            break;
          case "Reader":
            outputs.push(new Output(module.serial_number * 256, `Relais auxiliaire ${module.label}`));
            break;
        }
      });

      return outputs;
    }

    getAvailableInputs() {
      let max_inputs = 0;
      switch(this.equipement_type) {
        case "Hub2": 
          max_inputs = 6;
          break;
        case "Hub6": 
          max_inputs = 12;
          break;
        default:
          return [];
      }
      let inputs = [];
      for(let i = 1; i <= max_inputs; i++) {
        const label = i18n.global.t('hub.auxiliaryInput', { n: i });
        const input = new Input(i, label);
        inputs.push(input);
      }
      this.extensions.forEach(module => {
        for(let i = 1; i <= 6; i++) {
          const label = i18n.global.t('hub.auxiliaryInputModule', { n: i, module: module.label });
          const input = new Input(module.serial_number * 256 + i, label);
          inputs.push(input);
        }
      });
      return inputs;
    }

    // Same rule as the Haxo Setup app: licenses only count on a hub with an integrated license (LINK)
    hasLicense(name) {
      if(!this.integrated_license) return false;
      if(this.integrated_license.name === name) return true;
      return this.licenses.some(license => license.name === name);
    }

    get integrations() {
      const integrations = [];
      if(this.hasLicense("AJAX")) integrations.push(new Integration("AJAX", this.id, this.ajax_state));
      if(this.hasLicense("SPC")) integrations.push(new Integration("SPC", this.id, this.spc_state));
      if(this.hasLicense("SmartIntego")) integrations.push(new Integration("SmartIntego", this.id));
      if(this.hasLicense("UZ")) integrations.push(new Integration("UZ", this.id));
      if(this.hasLicense("Aperio")) integrations.push(new Integration("Aperio", this.id));
      if(this.hasLicense("Video")) integrations.push(new Integration("Video", this.id));
      return integrations;
    }

    // "Connected since ..." / "Disconnected since ..." as in the app, null when the backend gave no date
    get stateSinceText() {
      if(!this.hub_state_since) return null;
      const date = formatSinceDate(this.hub_state_since);
      return this.hub_state === 1 ? i18n.global.t('common.connectedSince', { date }) : i18n.global.t('common.disconnectedSince', { date });
    }

    // UZ or Aperio hubs already known through the readers paired on this LINK, one entry per address
    getMecaGateways(provider) {
      const gateways = [];
      for(const reader of this.readers || []) {
        const meca = reader.meca_config;
        if(meca && meca.provider === provider && !gateways.some(g => g.ip === meca.gateway_ip)) {
          gateways.push({ ip: meca.gateway_ip, name: meca.gateway_name });
        }
      }
      return gateways;
    }

    // Aperio locks already declared: the lock MAC is stored as product_serial, its device group id as product_address
    getDeclaredAperioLocks() {
      const declared = new Set();
      for(const reader of this.readers || []) {
        const meca = reader.meca_config;
        if(meca && meca.provider === 'Aperio') {
          if(meca.product_serial != null) declared.add(meca.product_serial);
          declared.add(String(meca.product_address));
        }
      }
      return declared;
    }

    get totalReaders() {
      return this.readers.length;
    }

    get totalExtensions() {
      return this.extensions.length;
    }

    get image() {
      switch(this.equipement_type) {
        case "Hub2":
          return "assets/images/hub2.png";
        case "Hub6":
          return "assets/images/hub6.png";
        case "BoxInteg":
          return "assets/images/link.png";
        default:
          return "";
      }
    }

    get documentationUrl() {
      return "assets/datasheets/hub2.pdf";
    }

    get productName() {
      switch(this.equipement_type) {
        case "Hub2": 
        case "Hub6": 
          return "HUB";
        case "BoxInteg":
          return "LINK";
        default:
          return "";
      }
    }

    path() {
      switch(this.equipement_type) {
        case "Hub2": 
        case "Hub6": 
          return {path: '/component-edit-hub', params: { facilityId: this.facility_id, id: this.id }};
        case "BoxInteg":
          return {path: '/component-edit-link', params: { facilityId: this.facility_id, id: this.id }};
        default:
          return "";
      }
    }

    addScenario(scenario) {
      this.scenarios.push(scenario);
    }

    updateScenario(scenario) {
      const index = this.scenarios.findIndex(s => s.id === scenario.id);
      if(index != -1) {
        this.scenarios[index] = scenario;
      }
    }
}
  